import axios from 'axios';
import { loggerAuthUrl, loggerEndpoint } from '../../reducers/index';

// Log levels
export const LOG_LEVELS = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3
};

const LOG_LEVEL_NAMES = {
    0: 'DEBUG',
    1: 'INFO',
    2: 'WARN',
    3: 'ERROR'
};

class Logger {
    constructor() {
        this.logFile = this.log_file();

        this.currentLogLevel = process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;
        this.maxLocalStorageSize = 1000; // Maximum number of logs to store locally

        // Server-side logging enabled-state, learned from the /logger/auth response.
        // null = not yet checked. Cached briefly so we don't hit /auth on every log
        // while logging is disabled (the default state).
        this.serverLoggingEnabled = null;
        this.lastEnabledCheck = 0;
        this.enabledCheckTTL = 30000; // ms

        this.logs = this.getStoredLogs();
        this.sessionId = this.generateSessionId();
        this.userAgent = navigator.userAgent;
        this.url = window.location.href;

        // Bind methods to preserve context
        this.log = this.log.bind(this);
        this.debug = this.debug.bind(this);
        this.info = this.info.bind(this);
        this.warn = this.warn.bind(this);
        this.error = this.error.bind(this);
    }

    log_file() {
        const prefix = process.env.REACT_APP_JS_LOGGING_FILENAME || 'libro_js_logs';
        const date = new Date().toISOString().split('T')[0];
        const extension = 'json';

        return {
            localStorageKey: prefix,
            filePrefix: prefix,
            fileDate: date,
            fileExt: extension,
            fullFileName: `${prefix}_${date}.${extension}`
        };
    };

    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    getStoredLogs() {
        try {
            if (!this.logFile) return [];
            if (!localStorage.getItem(this.logFile.localStorageKey)) return [];

            const stored = localStorage.getItem(this.logFile.localStorageKey);
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.warn('Failed to retrieve stored logs:', e);
            return [];
        }
    }

    saveLogsToLocalStorage() {
        try {
            // Keep only the most recent logs to avoid localStorage size limits
            const logsToStore = this.logs.slice(-this.maxLocalStorageSize);
            localStorage.setItem(this.logFile.localStorageKey, JSON.stringify(logsToStore));
        } catch (e) {
            console.warn('Failed to save logs to localStorage:', e);
            // If storage is full, clear old logs and try again
            try {
                this.logs = this.logs.slice(-500); // Keep only 500 most recent
                localStorage.setItem(this.logFile.localStorageKey, JSON.stringify(this.logs));
            } catch (e2) {
                console.warn('Failed to save logs after cleanup:', e2);
            }
        }
    }

    async sendLogToServer(logEntry) {
        if (!loggerEndpoint) return;

        // If we recently confirmed logging is disabled server-side, skip the
        // round-trip entirely until the cache expires.
        if (this.serverLoggingEnabled === false &&
            (Date.now() - this.lastEnabledCheck) < this.enabledCheckTTL) {
            return;
        }

        try {
            axios({
                url: loggerAuthUrl,
                method: 'get'
            })
            .then(response => {
                this.lastEnabledCheck = Date.now();

                // Honour the server's enabled flag. If the field is absent (older
                // server that predates this change), fall back to posting — the
                // server still gates saves via confirm_logging_enabled().
                const enabled = response.data ? response.data.enabled : undefined;
                this.serverLoggingEnabled = (enabled === undefined) ? true : (enabled === true);

                if (!this.serverLoggingEnabled) {
                    return; // Logging disabled server-side; don't POST
                }

                axios({
                    url: loggerEndpoint,
                    method: 'post',
                    data: {
                        ...logEntry,
                        sessionId: this.sessionId,
                        userAgent: this.userAgent,
                        url: this.url
                    },
                    headers: {
                        'Authorization': `Bearer ${response.data.token}`
                    }
                })
                .catch(() => {
                    console.warn('Failed to send log entry to server');
                })
            })
            .catch(() => {
                console.warn('Failed to authenticate with logger endpoint');
            })
        } catch (error) {
            // Don't log server submission errors to avoid infinite loops
            console.warn('Failed to send log to server:', error.message);
        }
    }

    log(level, message, data = {}, shouldSendToServer = true) {
        if (level < this.currentLogLevel) return;

        const timestamp = new Date().toISOString();
        const logEntry = {
            timestamp,
            level: LOG_LEVEL_NAMES[level],
            message,
            data,
            sessionId: this.sessionId,
            url: window.location.href,
            userAgent: this.userAgent
        };

        // Add to local storage
        this.logs.push(logEntry);
        this.saveLogsToLocalStorage();

        // Console output with styling
        const consoleOutputEnabled = process.env.REACT_APP_JS_INCLUDE_CONSOLE_OUTPUT ? process.env.REACT_APP_JS_INCLUDE_CONSOLE_OUTPUT === 'true' : false;
        if (consoleOutputEnabled) {
            const consoleMethod = level >= LOG_LEVELS.ERROR ? 'error' :
                                level >= LOG_LEVELS.WARN ? 'warn' :
                                level >= LOG_LEVELS.INFO ? 'info' : 'debug';

            console[consoleMethod](`[${LOG_LEVEL_NAMES[level]}] ${message}`, data);
        }

        // Send to server
        if (shouldSendToServer && level >= LOG_LEVELS.DEBUG) {
            this.sendLogToServer(logEntry);
        }

        return logEntry;
    }

    debug(message, data = {}) {
        return this.log(LOG_LEVELS.DEBUG, message, data, false);
    }

    info(message, data = {}) {
        return this.log(LOG_LEVELS.INFO, message, data, true);
    }

    warn(message, data = {}) {
        return this.log(LOG_LEVELS.WARN, message, data, true);
    }

    error(message, data = {}) {
        return this.log(LOG_LEVELS.ERROR, message, data, true);
    }

    // Specialized logging methods for common scenarios
    logUserAction(action, component, data = {}) {
        this.info(`User Action: ${action}`, {
            component,
            action,
            ...data
        });
    }

    logApiCall(method, url, data = {}, response = null, error = null) {
        if (error) {
            this.error(`API Error: ${method} ${url}`, {
                method,
                url,
                requestData: data,
                error: error.message || error,
                status: error.response && error.response.status ? error.response.status : null,
                responseData: error.response && error.response.data ? error.response.data : null
            });
        } else {
            this.info(`API Call: ${method} ${url}`, {
                method,
                url,
                requestData: data,
                responseStatus: response && response.status ? response.status : null,
                responseData: response && response.data ? response.data : null
            });
        }
    }

    logCalculation(calculatorType, inputs, outputs, errors = []) {
        const logLevel = errors.length > 0 ? LOG_LEVELS.ERROR : LOG_LEVELS.INFO;
        this.log(logLevel, `Calculation: ${calculatorType}`, {
            calculatorType,
            inputs,
            outputs,
            errors,
            timestamp: new Date().toISOString()
        });
    }

    logFormSubmission(formName, formData, success = true, error = null) {
        if (success) {
            this.info(`Form Submitted: ${formName}`, {
                formName,
                formData,
                success: true
            });
        } else {
            this.error(`Form Submission Failed: ${formName}`, {
                formName,
                formData,
                success: false,
                error: error && error.message ? error.message : error
            });
        }
    }

    // Utility methods
    exportLogs() {
        const logsJson = JSON.stringify(this.logs, null, 2);
        const blob = new Blob([logsJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = this.logFile.fullFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        this.info('Logs exported', {
            logCount: this.logs.length,
            sessionId: this.sessionId
        });
    }

    clearLogs() {
        this.logs = [];
        localStorage.removeItem(this.logFile.localStorageKey);
        this.info('Logs cleared');
    }

    getLogs(filterLevel = null, limit = null) {
        let filteredLogs = this.logs;

        if (filterLevel !== null) {
            const levelNumber = typeof filterLevel === 'string' ?
                LOG_LEVELS[filterLevel.toUpperCase()] : filterLevel;
            filteredLogs = this.logs.filter(log =>
                LOG_LEVELS[log.level] >= levelNumber
            );
        }

        if (limit) {
            filteredLogs = filteredLogs.slice(-limit);
        }

        return filteredLogs;
    }

    getLogStats() {
        const stats = {
            total: this.logs.length,
            byLevel: {},
            sessionId: this.sessionId,
            oldestLog: this.logs.length > 0 ? this.logs[0].timestamp : null,
            newestLog: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null
        };

        // Count by level
        Object.values(LOG_LEVEL_NAMES).forEach(level => {
            stats.byLevel[level] = this.logs.filter(log => log.level === level).length;
        });

        return stats;
    }
}

// Create singleton instance
const logger = new Logger();

// Global error handler
window.addEventListener('error', (event) => {
    logger.error('Uncaught JavaScript Error', {
        message: event.error && event.error.message ? event.error.message : event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error && event.error.stack ? event.error.stack : null
    });
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled Promise Rejection', {
        reason: event.reason && event.reason.message ? event.reason.message : event.reason,
        stack: event.reason && event.reason.stack ? event.reason.stack : null
    });
});

export default logger;